import React, { useCallback, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';

import {
	CHECKLIST_PURPOSE,
	MY_DAY_CONFIRMATION_KEY,
	PICK_LIST_RECALCULATION_MODE,
} from '../../../common/constants';
import { getFullMachineName } from '../../../common/utils/machine';
import { MY_DAY_STEP_STATUS } from '../../../common/utils/myDay/driverDaySteps';

import {
	Button,
	Label,
	Text,
} from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { getVehicleText } from '../servicerun/ServiceRunDetail/ServiceRunVehicleDetail';
import { createDriverMyDayFocusCardStyles } from './DriverMyDayFocusCard.styles';
import DriverRouteMap from './DriverRouteMap';
import OnTheRunSection from './board/OnTheRunSection';
import ReturnStockList from './board/ReturnStockList';
import StepFocusCard from './board/StepFocusCard';
import StopsList from './board/StopsList';

export const FOCUS_KEY_CURRENT_STOP = 'currentStop';
export const FOCUS_KEY_START_RUN = 'startServiceRun';

// Focus card of the classic My day board: the focused step, the current stop or
// the start run prompt. day and actions are the shared driver day hooks
// (useDriverDayModel, useDriverDayActions) from DriverMyDayView.
const DriverMyDayFocusCard = ({
	day,
	actions,
	user,
	trainingContext,
	focusKey,
	isFocusOverridden,
	clearFocusOverride,
	openBrief,
	openVehicleDialog,
	openNextStopDialog,
	onExitTraining,
}) => {
	const styles = useThemedStyles(createDriverMyDayFocusCardStyles);
	const { theme } = useTheme();
	const {
		allAssignedRunsComplete,
		todayDateKey,
		serviceRun,
		isTraining,
		isOnline,
		trainingOffline,
		canEditPickLists,
		canCreateTicket,
		sortedDockets,
		isDocketDone,
		nextIncompleteDocket,
		activeDocket,
		upcomingDocket,
		routeMapPoints,
		reorderableStops,
		skipPickStepForDocket,
		skipRestockStepForDocket,
		perStopSteps,
		endOfDaySteps,
		stepByKey,
		runStarted,
		startOfDayProgress,
		endOfDayProgress,
		completedStopsCount,
		pickRows,
		accessKeyRows,
		ticketRows,
		moneyBagRows,
		freshFoodStops,
	} = day;
	const {
		openStopDetails,
		openPickList,
		openTickets,
		openQuickPhoto,
		openCreateTicket,
		openFullRouteMap,
		goToNextStop: goToNextStopInMyDay,
		openClockPass,
		openChecklist,
		openRecalculatePicklistAlert,
		recalculatingPickListsMode,
		confirmStep,
		isConfirmingStep,
		markPickTopUpNotNeeded,
		confirmTubsLoaded,
		startServiceRun,
		isConfirmingOnlineStep,
		confirmReturnedToWarehouse,
		isConfirmingReturnedToWarehouse,
		onPerStopStepPress,
	} = actions;

	const currentStopDetailStyles = useMemo(() => ({
		headerText: {
			fontSize: 18,
			marginTop: 2,
			color: theme.text1,
		},
		detailView: {
			flexDirection: 'column',
			flex: 1,
			flexWrap: 'wrap',
			marginBottom: 4,
		},
		separator: {
			marginLeft: -8,
			marginRight: -8,
			marginBottom: 8,
			marginTop: 8,
		},
		icon: {
			width: 26,
			alignItems: 'center',
		},
		typographyParagraph: {
			fontSize: 16,
			color: theme.text1,
		},
	}), [theme.text1]);

	const getPhaseKicker = useCallback((key) => {
		if (key === FOCUS_KEY_CURRENT_STOP || key === FOCUS_KEY_START_RUN) {
			return `On the run · ${completedStopsCount}/${sortedDockets.length} stops done`;
		}

		const isEndStep = endOfDaySteps.some(step => step.key === key);
		const { doneCount, totalCount } = isEndStep ? endOfDayProgress : startOfDayProgress;
		return `${isEndStep ? 'End of day' : 'Start of day'} · ${doneCount}/${totalCount} done`;
	}, [completedStopsCount, endOfDayProgress, endOfDaySteps, sortedDockets.length, startOfDayProgress]);

	const renderFocusListRow = useCallback(({ key, text, meta, label, onPress }) => {
		const content = (
			<>
				<Text style={styles.focusRowText}>{text}</Text>
				{Boolean(meta) && <Text style={styles.focusRowMeta}>{meta}</Text>}
				{label}
			</>
		);

		if (onPress) {
			return (
				<TouchableOpacity key={key} style={styles.focusRow} activeOpacity={0.86} onPress={onPress}>
					{content}
				</TouchableOpacity>
			);
		}

		return (
			<View key={key} style={styles.focusRow}>
				{content}
			</View>
		);
	}, [styles]);

	// Everything done: celebrate instead of prompting.
	if (!focusKey) {
		// A run without stops never "starts", so avoid claiming the day is done.
		if (!sortedDockets.length && !runStarted) {
			return (
				<StepFocusCard
					kicker="My day"
					title="All set"
					description="No stops are on this run yet. See your manager if you are expecting some."
				/>
			);
		}

		return (
			<StepFocusCard
				kicker={isTraining ? 'Training mode' : 'My day'}
				title={isTraining
					? allAssignedRunsComplete ? 'Training workflow complete' : 'Run training complete'
					: 'Day complete'}
				description={isTraining
					? allAssignedRunsComplete
						? `${trainingContext.targetDriverName} still needs to complete any personal driver actions.`
						: `This run is complete. Choose another of ${trainingContext.targetDriverName}'s assigned runs before completing the final return steps.`
					: 'Everything on your board is done. Nice work.'}
				statusLabel={{ label: isTraining ? 'Run done' : 'All done', props: { green: true } }}
				primaryAction={isTraining ? {
					label: 'Back to employee preview',
					onPress: onExitTraining,
				} : undefined}
			/>
		);
	}

	if (focusKey === FOCUS_KEY_CURRENT_STOP) {
		return (
			<OnTheRunSection
				activeDocket={activeDocket}
				freshFoodStop={freshFoodStops.find(stop => stop.serviceDocketId === activeDocket?.id)}
				onOpenPickList={openPickList}
				upcomingDocket={upcomingDocket}
				sortedDockets={sortedDockets}
				isDocketDone={isDocketDone}
				perStopSteps={perStopSteps}
				canCreateTicket={canCreateTicket}
				currentStopDetailStyles={currentStopDetailStyles}
				onStepPress={onPerStopStepPress}
				onOpenStopDetails={openStopDetails}
				onOpenQuickPhoto={openQuickPhoto}
				onOpenCreateTicket={openCreateTicket}
				onOpenTickets={openTickets}
				onGoToNextStop={goToNextStopInMyDay}
				onOpenFullRouteMap={openFullRouteMap}
				onSkipPickStep={isTraining ? undefined : skipPickStepForDocket}
				onSkipRestockStep={isTraining ? undefined : skipRestockStepForDocket}
				onOpenVisitDifferentStop={reorderableStops.length > 1 ? openNextStopDialog : undefined}
			/>
		);
	}

	if (focusKey === FOCUS_KEY_START_RUN) {
		const firstStopName = nextIncompleteDocket
			? getFullMachineName(nextIncompleteDocket.machine, nextIncompleteDocket.machine.place, nextIncompleteDocket.machine.location)
			: null;
		const requiresOnlinePickReadinessCheck = Boolean(serviceRun?.myDayPickTopUpStatus?.markedNotNeeded);

		return (
			<StepFocusCard
				kicker={getPhaseKicker(FOCUS_KEY_START_RUN)}
				title="Start service run"
				description={firstStopName ? `Everything is ready. First stop: ${firstStopName}.` : 'Everything is ready.'}
				primaryAction={{
					label: requiresOnlinePickReadinessCheck && !isOnline ? 'Connect to start service run' : 'Start service run',
					onPress: startServiceRun,
					loading: requiresOnlinePickReadinessCheck ? isConfirmingOnlineStep : isConfirmingStep,
					disabled: trainingOffline || (requiresOnlinePickReadinessCheck && !isOnline),
				}}
				secondaryAction={reorderableStops.length > 1 ? {
					label: 'Reorder remaining stops',
					onPress: openNextStopDialog,
					disabled: trainingOffline,
				} : undefined}
			/>
		);
	}

	const step = stepByKey[focusKey];
	if (!step) {
		return null;
	}

	const isDone = step.status === MY_DAY_STEP_STATUS.DONE;
	const cardBase = {
		kicker: getPhaseKicker(step.key),
		title: step.label,
		description: step.description,
		badgeCount: step.badgeCount,
		statusLabel: isDone ? { label: 'Done', props: { green: true } } : undefined,
		secondaryAction: isFocusOverridden ? { label: 'Back to next step', onPress: clearFocusOverride } : undefined,
	};

	switch (step.key) {
		case 'clockIn':
		case 'clockOut':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{ label: 'Open shift pass', onPress: openClockPass }}
				/>
			);
		case 'returnedToWarehouse':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: isOnline ? 'Confirm returned to warehouse' : 'Connect to confirm return',
						onPress: confirmReturnedToWarehouse,
						loading: isConfirmingReturnedToWarehouse,
						disabled: !isOnline || !step.canConfirm,
					} : undefined}
				/>
			);
		case 'brief':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{ label: 'Open brief', onPress: openBrief }}
				/>
			);
		case 'runOverview':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'Confirm run overview',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.RUN_OVERVIEW),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				>
					<View style={styles.focusContent}>
						<StopsList
							sortedDockets={sortedDockets}
							currentDocketId={nextIncompleteDocket?.id}
							isDocketDone={isDocketDone}
							onOpenStopDetails={openStopDetails}
						/>
						{
							// Escape hatch stays on the outline style so the solid
							// "Confirm run overview" action reads as the way forward.
							reorderableStops.length > 1 && (
								<Button
									label="Reorder remaining stops"
									onPress={openNextStopDialog}
									disabled={trainingOffline}
									style={styles.focusInlineButton}
								/>
							)
						}
						<DriverRouteMap
							points={routeMapPoints}
							mapHeight={180}
							onPress={openFullRouteMap}
							interactive={false}
						/>
					</View>
				</StepFocusCard>
			);
		case 'vehicle':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{
						label: serviceRun?.vehicle?.id ? `Confirm ${getVehicleText(serviceRun.vehicle)}` : 'Assign vehicle',
						onPress: openVehicleDialog,
						disabled: trainingOffline,
					}}
				/>
			);
		case 'packAndPick': {
			const packAndPickPrimaryAction = step.canMarkTopUpNotNeeded
				? {
					label: isOnline ? 'Top-up not needed' : 'Connect to mark top-up not needed',
					onPress: markPickTopUpNotNeeded,
					loading: isConfirmingOnlineStep,
					disabled: !isOnline,
				}
				: step.canConfirmTubsLoaded ? {
					label: isOnline ? 'I\'ve loaded all tubs into the van' : 'Connect to confirm tubs are loaded',
					onPress: confirmTubsLoaded,
					loading: isConfirmingOnlineStep,
					disabled: !isOnline,
				} : undefined;
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={packAndPickPrimaryAction}
				>
					<View style={styles.focusContent}>
						{
							canEditPickLists && (
								<>
									<Button
										label="Recalculate unfinished packs"
										onPress={() => openRecalculatePicklistAlert(PICK_LIST_RECALCULATION_MODE.UNFINISHED)}
										loading={recalculatingPickListsMode === PICK_LIST_RECALCULATION_MODE.UNFINISHED}
										disabled={trainingOffline || Boolean(recalculatingPickListsMode)}
										style={styles.focusInlineButton}
									/>
									<Text style={styles.focusActionHint}>
										Update pending and part-picked packs while keeping quantities already picked.
									</Text>
									<Button
										label="Recalculate and top up all"
										onPress={() => openRecalculatePicklistAlert(PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL)}
										loading={recalculatingPickListsMode === PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL}
										disabled={trainingOffline || Boolean(recalculatingPickListsMode)}
										style={styles.focusInlineButton}
									/>
									<Text style={styles.focusActionHint}>
										Check every unserviced pack and add only newly required items.
									</Text>
								</>
							)
						}
						{
							pickRows.map(row => renderFocusListRow({
								key: row.docket.id,
								text: row.stopName,
								label: (
									<Label
										label={row.pickStatusLabel}
										green={row.picked}
										yellow={row.incompletePick}
										primary={!row.picked && !row.incompletePick}
										style={styles.focusRowLabel}
									/>
								),
								onPress: () => openPickList(row.docket),
							}))
						}
					</View>
				</StepFocusCard>
			);
		}
		case 'accessKeys':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'Keys packed and site access reviewed',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.ACCESS_KEYS),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				>
					<View style={styles.focusContent}>
						{
							accessKeyRows.map(row => (
								<View key={row.id} style={styles.ticketGroup}>
									<Text style={styles.accessStopName}>{row.stopName}</Text>
									<Text style={styles.ticketDescription}>{row.requirementLabels.join(' · ')}</Text>
								</View>
							))
						}
					</View>
				</StepFocusCard>
			);
		case 'ticketsSummary':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'I\'ve read all tickets',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.TICKETS_SUMMARY),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				>
					<View style={styles.focusContent}>
						{
							ticketRows.map(row => (
								<TouchableOpacity
									key={row.docket.id}
									style={styles.ticketGroup}
									activeOpacity={0.86}
									onPress={() => openTickets(row.docket)}
								>
									<View style={styles.ticketGroupHeader}>
										<Text style={styles.focusRowText}>{row.stopName}</Text>
										<Label label={`${row.ticketCount} tickets`} primary={true} style={styles.focusRowLabel} />
									</View>
									{
										row.descriptions.map((description, index) => (
											<Text key={index} style={styles.ticketDescription} numberOfLines={2}>
												{`• ${description}`}
											</Text>
										))
									}
								</TouchableOpacity>
							))
						}
					</View>
				</StepFocusCard>
			);
		case 'leaveWarehouseChecklist':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{
						label: 'Open leave warehouse checklist',
						onPress: () => openChecklist(CHECKLIST_PURPOSE.LEAVE_WAREHOUSE, 'Leave warehouse checklist'),
					}}
				/>
			);
		case 'moneyBags':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'All money bags returned',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.MONEY_BAGS),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				>
					<View style={styles.focusContent}>
						{
							moneyBagRows.map(row => renderFocusListRow({
								key: row.id,
								text: row.stopName,
								meta: row.cashBag ? undefined : 'Collect cash',
								label: row.cashBag ? <Label label={String(row.cashBag)} primary={true} style={styles.focusRowLabel} /> : undefined,
							}))
						}
					</View>
				</StepFocusCard>
			);
		case 'returnKeys':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'Keys and passes returned',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.RETURN_KEYS),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				/>
			);
		case 'returnStock':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={!isDone ? {
						label: 'Stock returned',
						onPress: () => confirmStep(MY_DAY_CONFIRMATION_KEY.RETURN_STOCK),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					} : undefined}
				>
					<ReturnStockList
						driverId={isTraining ? serviceRun?.driver?.id : user?.id}
						runDate={todayDateKey}
						noCache={isTraining}
					/>
				</StepFocusCard>
			);
		case 'vehicleChecklist':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{
						label: 'Open vehicle checklist',
						onPress: () => openChecklist(CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY, 'Vehicle end of day checklist'),
					}}
				/>
			);
		case 'selfDebrief':
			return (
				<StepFocusCard
					{...cardBase}
					primaryAction={{
						label: 'Open self debrief',
						onPress: () => openChecklist(CHECKLIST_PURPOSE.SELF_DEBRIEF, 'Self debrief'),
					}}
				/>
			);
		default:
			return <StepFocusCard {...cardBase} />;
	}
};

export default DriverMyDayFocusCard;
